import React, { useState } from 'react';
import './JsonViewer.css';

const JsonViewer = ({ data, title = "JSON Data", defaultCollapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [copySuccess, setCopySuccess] = useState(false);
  const [collapsedPaths, setCollapsedPaths] = useState(new Set());

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = jsonString;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const toggleCollapse = (path) => {
    const newCollapsedPaths = new Set(collapsedPaths);
    if (newCollapsedPaths.has(path)) {
      newCollapsedPaths.delete(path);
    } else {
      newCollapsedPaths.add(path);
    }
    setCollapsedPaths(newCollapsedPaths);
  };

  // Функция для сбора всех путей в JSON
  const getAllPaths = (obj, currentPath = '', paths = new Set()) => {
    if (obj && typeof obj === 'object') {
      if (currentPath) {
        paths.add(currentPath);
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          getAllPaths(item, `${currentPath}[${index}]`, paths);
        });
      } else {
        Object.keys(obj).forEach(key => {
          const newPath = currentPath ? `${currentPath}.${key}` : key;
          getAllPaths(obj[key], newPath, paths);
        });
      }
    }
    return paths;
  };

  const collapseAll = () => {
    const allPaths = getAllPaths(data, 'root');
    setCollapsedPaths(allPaths);
  };

  const renderJsonValue = (value, path = '', depth = 0) => {
    const indent = '  '.repeat(depth);

    if (value === null) {
      return <span className="json-null">null</span>;
    }

    if (typeof value === 'string') {
      return <span className="json-string">"{value}"</span>;
    }

    if (typeof value === 'number') {
      return <span className="json-number">{value}</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="json-boolean">{value.toString()}</span>;
    }

    if (Array.isArray(value)) {
      const isCollapsed = collapsedPaths.has(path);
      const itemCount = value.length;

      return (
        <span>
          <button
            className="json-collapse-btn"
            onClick={() => toggleCollapse(path)}
            type="button"
          >
            <span className={`json-arrow ${isCollapsed ? 'collapsed' : 'expanded'}`}>▶</span>
          </button>
          <span className="json-bracket">[</span>
          {isCollapsed ? (
            <span className="json-collapsed">
              <span className="json-ellipsis"> ...{itemCount} items </span>
              <span className="json-bracket">]</span>
            </span>
          ) : (
            <>
              {itemCount > 0 && <br />}
              {value.map((item, index) => (
                <div key={index} style={{ marginLeft: `${(depth + 1) * 20}px` }}>
                  {renderJsonValue(item, `${path}[${index}]`, depth + 1)}
                  {index < value.length - 1 && <span className="json-comma">,</span>}
                  {index < value.length - 1 && <br />}
                </div>
              ))}
              {itemCount > 0 && (
                <div style={{ marginLeft: `${depth * 20}px` }}>
                  <span className="json-bracket">]</span>
                </div>
              )}
              {itemCount === 0 && <span className="json-bracket">]</span>}
            </>
          )}
        </span>
      );
    }

    if (typeof value === 'object') {
      const isCollapsed = collapsedPaths.has(path);
      const keys = Object.keys(value);
      const keyCount = keys.length;

      return (
        <span>
          <button
            className="json-collapse-btn"
            onClick={() => toggleCollapse(path)}
            type="button"
          >
            <span className={`json-arrow ${isCollapsed ? 'collapsed' : 'expanded'}`}>▶</span>
          </button>
          <span className="json-bracket">{"{"}</span>
          {isCollapsed ? (
            <span className="json-collapsed">
              <span className="json-ellipsis"> ...{keyCount} keys </span>
              <span className="json-bracket">{"}"}</span>
            </span>
          ) : (
            <>
              {keyCount > 0 && <br />}
              {keys.map((key, index) => (
                <div key={key} style={{ marginLeft: `${(depth + 1) * 20}px` }}>
                  <span className="json-key">"{key}"</span>
                  <span className="json-colon">: </span>
                  {renderJsonValue(value[key], `${path}.${key}`, depth + 1)}
                  {index < keys.length - 1 && <span className="json-comma">,</span>}
                  {index < keys.length - 1 && <br />}
                </div>
              ))}
              {keyCount > 0 && (
                <div style={{ marginLeft: `${depth * 20}px` }}>
                  <span className="json-bracket">{"}"}</span>
                </div>
              )}
              {keyCount === 0 && <span className="json-bracket">{"}"}</span>}
            </>
          )}
        </span>
      );
    }

    return <span>{String(value)}</span>;
  };

  return (
    <div className="json-viewer">
      <div className="json-viewer-header">
        <button
          type="button"
          className="json-viewer-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <span className={`json-viewer-arrow ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            ▶
          </span>
          {title}
        </button>
        <div className="json-viewer-actions">
          <button
            type="button"
            className="json-viewer-expand-all"
            onClick={() => setCollapsedPaths(new Set())}
            title="Expand all"
          >
            ⊞ Expand All
          </button>
          <button
            type="button"
            className="json-viewer-expand-all"
            onClick={collapseAll}
            title="Collapse all"
          >
            ⊟ Collapse All
          </button>
          <button
            type="button"
            className={`json-viewer-copy ${copySuccess ? 'success' : ''}`}
            onClick={handleCopy}
            title="Copy to clipboard"
          >
  {copySuccess ? '✓ Copied' : '📋  Copy'}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="json-viewer-content">
          <div className="json-viewer-interactive">
            {renderJsonValue(data, 'root', 0)}
          </div>
        </div>
      )}
    </div>
  );
};

export default JsonViewer;